import axios from "axios";
import { API_BASE_URL } from "utils/api";

/**
 * Staff Management API Service
 * Handles all API calls for staff CRUD operations and permission management.
 * Uses backend routes defined in API documentation.
 */

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add token to requests
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Get all staff members with pagination and filters
 * @param {number} page - Page number (default: 1)
 * @param {number} limit - Items per page (default: 10)
 * @param {Object} filters - Search and filter parameters
 * @returns {Promise} Response with staff list and total count
 */
export const getAllStaff = async (page = 1, limit = 10, filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (page) params.append("page", page);
    if (limit) params.append("limit", limit);
    if (filters.search) params.append("search", filters.search);
    if (filters.status) params.append("status", filters.status);
    if (filters.role) params.append("role", filters.role);

    const response = await axiosInstance.get(`/users/staff?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get single staff member by ID
 * @param {string} staffId - Staff member ID
 * @returns {Promise} Staff details
 */
export const getStaffById = async (staffId) => {
  try {
    const response = await axiosInstance.get(`/users/staff/${staffId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create new staff member
 * @param {Object} staffData - Staff data { name, email, phone, password, status }
 * @returns {Promise} Created staff object
 */
export const createStaff = async (staffData) => {
  try {
    const response = await axiosInstance.post("/users/staff", staffData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update staff member
 * @param {string} staffId - Staff member ID
 * @param {Object} staffData - Fields to update
 * @returns {Promise} Updated staff object
 */
export const updateStaff = async (staffId, staffData) => {
  try {
    const response = await axiosInstance.put(`/users/staff/${staffId}`, staffData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete staff member
 * @param {string} staffId - Staff member ID
 * @returns {Promise} Success response
 */
export const deleteStaff = async (staffId) => {
  try {
    const response = await axiosInstance.delete(`/users/staff/${staffId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get permissions for a staff member
 * @param {string} staffId - Staff member ID
 * @returns {Promise} Permissions object
 */
export const getStaffPermissions = async (staffId) => {
  try {
    const response = await axiosInstance.get(`/users/staff/${staffId}/permissions`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update permissions for a staff member
 * @param {string} staffId - Staff member ID
 * @param {Object} permissions - Permissions object
 * @returns {Promise} Updated permissions
 */
export const updateStaffPermissions = async (staffId, permissions) => {
  try {
    const response = await axiosInstance.put(`/users/staff/${staffId}/permissions`, {
      permissions,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const staffAPI = {
  getAllStaff,
  getStaffById,
  createStaff,
  updateStaff,
  deleteStaff,
  getStaffPermissions,
  updateStaffPermissions,
};

export default staffAPI;
